import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const ScheduleContext = createContext();

export const useSchedule = () => {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error('useSchedule must be used within a ScheduleProvider');
  }
  return context;
};

export const ScheduleProvider = ({ children }) => {
  const [userSchedule, setUserSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchUserSchedule = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_schedule')
        .select(`
          id,
          event_id,
          events (
            title,
            speaker,
            day,
            time,
            location,
            category,
            description,
            tags
          )
        `)
        .eq('user_id', user.id);

      if (error) {
        return;
      }

      const scheduleItems = data.map(item => ({
        id: item.id,
        eventId: item.event_id,
        title: item.events.title,
        speaker: item.events.speaker,
        day: item.events.day,
        time: item.events.time,
        location: item.events.location,
        category: item.events.category,
        description: item.events.description,
        tags: item.events.tags
      }));

      setUserSchedule(scheduleItems);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchUserSchedule();
    } else {
      setUserSchedule([]);
      setLoading(false);
    }
  }, [user, fetchUserSchedule]);

  const addToSchedule = async (event) => {
    if (!user) {
      return { success: false, message: 'User not authenticated' };
    }

    // Handle both 'id' and 'event_id' properties
    const eventId = event.id || event.event_id;
    
    if (!event || !eventId) {
      return { success: false, message: 'Invalid event data' };
    }

    // Validate user ID format (should be UUID)
    if (!user.id || typeof user.id !== 'string') {
      return { success: false, message: 'Invalid user ID' };
    }

    // Validate event ID format (should be number)
    if (!eventId || typeof eventId !== 'number') {
      return { success: false, message: 'Invalid event ID' };
    }

    try {
      // Check if event is already in schedule
      const isAlreadyScheduled = userSchedule.some(item => item.eventId === eventId);
      
      if (isAlreadyScheduled) {
        return { success: false, message: 'Event is already in your schedule' };
      }

      const { error } = await supabase
        .from('user_schedule')
        .insert({
          user_id: user.id,
          event_id: eventId
        })
        .select();

      if (error) {
        // Check if it's a duplicate key error
        if (error.code === '23505') {
          await fetchUserSchedule();
          return { success: true, message: 'Event is already in your schedule' };
        }
        
        // Check if it's a foreign key constraint error
        if (error.code === '23503') {
          return { success: false, message: 'Event or user not found' };
        }
        
        // Check if it's a table doesn't exist error
        if (error.code === '42P01') {
          return { success: false, message: 'Schedule table not set up. Please contact support.' };
        }
        return { success: false, message: `Failed to add event to schedule: ${error.message}` };
      }

      // Refresh schedule
      await fetchUserSchedule();
      
      // Refresh attendee counts directly
      await refreshAttendeeCounts();
      
      return { success: true, message: 'Event added to schedule' };
    } catch (error) {
      return { success: false, message: 'Failed to add event to schedule' };
    }
  };

  const removeFromSchedule = async (scheduleId) => {
    if (!user) return { success: false, message: 'User not authenticated' };

    try {
      // First, get the event_id from the user_schedule record
      const { data: scheduleRecord, error: fetchError } = await supabase
        .from('user_schedule')
        .select('event_id')
        .eq('id', scheduleId)
        .eq('user_id', user.id)
        .single();

      if (fetchError) {
        return { success: false, message: 'Failed to find schedule record' };
      }

      // Remove from user_schedule
      const { error: scheduleError } = await supabase
        .from('user_schedule')
        .delete()
        .eq('id', scheduleId)
        .eq('user_id', user.id);

      if (scheduleError) {
        return { success: false, message: 'Failed to remove from schedule' };
      }

      // Remove from event_signups
      const { error: signupError } = await supabase
        .from('event_signups')
        .delete()
        .eq('user_id', user.id)
        .eq('event_id', scheduleRecord.event_id);

      if (signupError) {
      }

      // Refresh schedule
      await fetchUserSchedule();
      
      // Refresh attendee counts directly
      await refreshAttendeeCounts();
      
      return { 
        success: true, 
        message: 'Event removed from schedule',
        eventId: scheduleRecord.event_id 
      };
    } catch (error) {
      return { success: false, message: 'Failed to remove event' };
    }
  };

  const isEventScheduled = (eventId) => {
    return userSchedule.some(item => item.eventId === eventId);
  };

  const getEventAttendeeCount = async (eventId) => {
    try {
      const { count, error } = await supabase
        .from('event_signups')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', eventId);

      if (error) {
        return 0;
      }

      return count || 0;
    } catch (error) {
      return 0;
    }
  };

  const cleanupAndSyncDatabases = async () => {
    if (!user) return { success: false, message: 'User not authenticated' };
    
    try {
      // Get all user's signups from event_signups
      const { data: signups, error: signupsError } = await supabase
        .from('event_signups')
        .select('event_id')
        .eq('user_id', user.id);
        
      if (signupsError) {
        return { success: false, message: 'Failed to fetch signups' };
      }
      
      // Get all user's schedule items from user_schedule
      const { data: scheduleItems, error: scheduleError } = await supabase
        .from('user_schedule')
        .select('event_id')
        .eq('user_id', user.id);
        
      if (scheduleError) {
        return { success: false, message: 'Failed to fetch schedule' };
      }
      
      // Clear both tables for this user
      await supabase
        .from('user_schedule')
        .delete()
        .eq('user_id', user.id);

      await supabase
        .from('event_signups')
        .delete()
        .eq('user_id', user.id);
      
      // Re-populate both tables with the union of both sets
      const allEventIds = new Set([
        ...(signups?.map(s => s.event_id) || []),
        ...(scheduleItems?.map(s => s.event_id) || [])
      ]);
      
      for (const eventId of allEventIds) {
        // Insert into user_schedule
        await supabase
          .from('user_schedule')
          .insert({
            user_id: user.id,
            event_id: eventId
          });
          
        // Insert into event_signups
        await supabase
          .from('event_signups')
          .insert({
            user_id: user.id,
            event_id: eventId
          });
      }
      
      // Refresh schedule
      await fetchUserSchedule();
      
      return { 
        success: true, 
        message: `Successfully synced ${allEventIds.size} events` 
      };
      
    } catch (error) {
      return { success: false, message: 'Failed to sync databases' };
    }
  };

  const refreshAttendeeCounts = async () => {
    // Try to call the global function if available
    if (window.refreshAttendeeCounts) {
      window.refreshAttendeeCounts();
    } else {
    }
  };

  const value = {
    userSchedule,
    addToSchedule,
    removeFromSchedule,
    isEventScheduled,
    getEventAttendeeCount,
    refreshAttendeeCounts,
    cleanupAndSyncDatabases,
    loading
  };

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
}; 